/**
 * Image and data helpers shared by the document OCR test and the face recognition test.
 */

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => resolve((e.target?.result as string).split(',')[1]);
    reader.readAsDataURL(file);
  });

/** A file as base64: images are scaled down to `maxWidth` and re-encoded as JPEG, a PDF is sent as is. */
export const compressImage = (file: File, maxWidth = 2000, quality = 0.8): Promise<string> => {
  if (file.type === 'application/pdf') return readFileAsBase64(file);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let w = img.width, h = img.height;
        if (w > maxWidth) { h = (maxWidth / w) * h; w = maxWidth; }
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality).split(',')[1]);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

/** Front above back on one white canvas, as one JPEG — the "two sides, combined" capture mode. */
export const combineImages = (front: string, back: string): Promise<string> =>
  new Promise((resolve) => {
    const imgF = new Image(); const imgB = new Image();
    let loaded = 0;
    const check = () => {
      if (++loaded < 2) return;
      const maxW = Math.max(imgF.width, imgB.width);
      const canvas = document.createElement('canvas');
      canvas.width = maxW; canvas.height = imgF.height + imgB.height;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = 'white'; ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(imgF, 0, 0); ctx.drawImage(imgB, 0, imgF.height);
      resolve(canvas.toDataURL('image/jpeg', 0.8).split(',')[1]);
    };
    imgF.onload = check; imgB.onload = check;
    imgF.src = `data:image/jpeg;base64,${front}`;
    imgB.src = `data:image/jpeg;base64,${back}`;
  });

/** A raw OCR field name split into its parts: `NORM_FRONT_ENG_FullName` → source, side, engine, field. */
export const parseFieldName = (name: string) => {
  const parts = name.split('_');
  const source = parts[0];
  if (source === 'NORM' && parts.length >= 4) {
    return { source, side: parts[1], engine: parts[2], field: parts.slice(3).join('_') };
  }
  return { source, side: null, engine: null, field: parts.slice(1).join('_') || name };
};

/** An image value from a response as something an `<img>` can show. */
export const asImageSrc = (b64: string) => (b64.startsWith('data:') ? b64 : `data:image/jpeg;base64,${b64}`);

const BINARY_KEYS = ['face', 'Face', 'signature', 'Signature', 'faceVector', 'FaceVector'];

/** A response as indented JSON, with the long base64 images summarised instead of printed. */
export const stringifyResponse = (data: unknown) =>
  JSON.stringify(data, (k, v) => {
    if (BINARY_KEYS.includes(k) && typeof v === 'string' && v.length > 100) return `[base64, ${v.length} chars]`;
    return v;
  }, 2);

/** A float vector as compact base64 binary (Float32Array bytes) — small enough for a QR code. */
export const floatsToBase64 = (values: number[]): string => {
  const bytes = new Uint8Array(new Float32Array(values).buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};
